import { useState, useMemo } from 'react'
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
  type DragOverEvent,
} from '@dnd-kit/core'
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import type { Project, KanbanData } from '@1130-studio/shared'
import { KanbanColumn } from './KanbanColumn'
import { ProjectCardDragOverlay } from './ProjectCard'
import { useMoveProject } from '../../api/projects'

interface KanbanBoardProps {
  data: KanbanData
  onAddProject?: (stageId: string) => void
}

export function KanbanBoard({ data, onAddProject }: KanbanBoardProps) {
  const [activeId, setActiveId] = useState<string | null>(null)
  const [localProjects, setLocalProjects] = useState<Record<string, Project[]>>(data.projects)

  const moveProject = useMoveProject()

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Find project across all stages
  const findProject = (id: string): Project | undefined => {
    for (const stageProjects of Object.values(localProjects)) {
      const project = stageProjects.find((p) => p.id === id)
      if (project) return project
    }
    return undefined
  }

  // Find which stage a project is in
  const findContainer = (id: string): string | undefined => {
    // First check if id is a stage id
    if (data.stages.some((s) => s.id === id)) {
      return id
    }

    // Otherwise find the project's container
    for (const [stageId, projects] of Object.entries(localProjects)) {
      if (projects.some((p) => p.id === id)) {
        return stageId
      }
    }
    return undefined
  }

  const activeProject = useMemo(() => {
    if (!activeId) return undefined
    return findProject(activeId)
  }, [activeId, localProjects])

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string)
  }

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event

    if (!over) return

    const activeId = active.id as string
    const overId = over.id as string

    const activeContainer = findContainer(activeId)
    const overContainer = findContainer(overId)

    if (!activeContainer || !overContainer || activeContainer === overContainer) {
      return
    }

    setLocalProjects((prev) => {
      const activeItems = [...(prev[activeContainer] || [])]
      const overItems = [...(prev[overContainer] || [])]

      const activeIndex = activeItems.findIndex((p) => p.id === activeId)
      const movedProject = activeItems[activeIndex]

      // Remove from source
      activeItems.splice(activeIndex, 1)

      // Add to destination
      const overIndex = overItems.findIndex((p) => p.id === overId)
      const newIndex = overIndex >= 0 ? overIndex : overItems.length

      overItems.splice(newIndex, 0, { ...movedProject, stageId: overContainer })

      return {
        ...prev,
        [activeContainer]: activeItems,
        [overContainer]: overItems,
      }
    })
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    setActiveId(null)

    if (!over) {
      // Reset to original data if dropped outside
      setLocalProjects(data.projects)
      return
    }

    const activeId = active.id as string
    const overId = over.id as string

    const activeContainer = findContainer(activeId)
    let overContainer = findContainer(overId)

    // If dropping on a stage directly (empty column)
    if (data.stages.some((s) => s.id === overId)) {
      overContainer = overId
    }

    if (!activeContainer || !overContainer) {
      setLocalProjects(data.projects)
      return
    }

    // Calculate new position
    const overItems = localProjects[overContainer] || []
    const overIndex = overItems.findIndex((p) => p.id === overId)
    const position = overIndex >= 0 ? overIndex : overItems.length

    // Call API to move project
    moveProject.mutate({
      id: activeId,
      data: {
        stageId: overContainer,
        position,
      },
    })
  }

  // Sync with server data when it changes
  useMemo(() => {
    if (!moveProject.isPending) {
      setLocalProjects(data.projects)
    }
  }, [data.projects])

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="kanban-wrapper flex-1 overflow-x-auto px-8 py-6 bg-gradient-to-br from-accent-lime/[0.03] via-transparent to-accent-violet/[0.03]">
        <div className="flex gap-5 min-w-max pb-6">
          {data.stages.map((stage) => (
            <KanbanColumn
              key={stage.id}
              stage={stage}
              projects={localProjects[stage.id] || []}
              onAddProject={onAddProject}
            />
          ))}
        </div>
      </div>

      <DragOverlay>
        {activeProject ? <ProjectCardDragOverlay project={activeProject} /> : null}
      </DragOverlay>
    </DndContext>
  )
}
